import Head from 'next/head';
import Link from 'next/link';

export default function Privacy() {
  const lastUpdated = "March 18, 2026";

  return (
    <>
      <Head>
        <title>Privacy Policy | L.I.S.T Learning Pathways</title>
        <meta name="description" content="Privacy Policy for L.I.S.T Learning Pathways - Learn about how we protect and handle your data." />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Document Hero */}
        <section className="bg-gray-50 border-b border-gray-100 pt-32 pb-20 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-gray-900">Privacy Policy</h1>
            <div className="flex items-center justify-center gap-4 text-gray-500 text-base">
              <span className="w-12 h-px bg-brand-primary/20"></span>
              <span>Last Updated: {lastUpdated}</span>
              <span className="w-12 h-px bg-brand-primary/20"></span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100">
              <div className="prose prose-lg max-w-none prose-headings:text-brand-dark prose-headings:font-extrabold prose-p:text-gray-600 prose-li:text-gray-600 prose-headings:tracking-tight">
                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6">1. Information We Collect</h2>
                <p>
                  At L.I.S.T Learning Pathways, we collect information that you provide directly to us, including when you create an account,
                  make a purchase, or communicate with us. This may include:
                </p>
                <ul className="grid md:grid-cols-2 gap-4 list-none pl-0">
                  {[
                    "Name and contact information",
                    "Payment information",
                    "Course progress and completion data",
                    "Communication preferences"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg text-base font-medium">
                      <div className="w-2 h-2 rounded-full bg-brand-primary shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12">2. How We Use Your Information</h2>
                <p>
                  We use the information we collect to provide a personalized leadership development experience. Specifically, we use it to:
                </p>
                <div className="space-y-4">
                  {[
                    "Provide and improve our leadership development services",
                    "Process your transactions securely",
                    "Send you learning updates and marketing communications",
                    "Analyze platform usage to optimize your user experience"
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-all group">
                      <span className="text-brand-primary font-black text-xl">0{i + 1}</span>
                      <p className="m-0 font-medium text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12">3. Data Security</h2>
                <p>
                  We implement institutional-grade technical and organizational security measures to protect your
                  personal information. Our systems utilize modern encryption and secure data handling practices.
                  However, no security system is impenetrable and we cannot guarantee 100% security against strategic-level breaches.
                </p>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12">4. Your Rights</h2>
                <p>
                  As a valued member of our academy, you have full control over your data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your personal data at any time via your dashboard</li>
                  <li>Correct inaccurate or outdated data</li>
                  <li>Request complete deletion of your data from our systems</li>
                  <li>Object to specific data processing activities</li>
                </ul>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12">5. Contact Us</h2>
                <div className="bg-brand-dark text-white p-8 rounded-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary/20 rounded-full blur-2xl"></div>
                  <p className="relative z-10 mb-6 font-medium">If you have any questions about this Privacy Policy, please reach out to our team:</p>
                  <div className="relative z-10 grid sm:grid-cols-2 gap-8">
                    <div>
                      <span className="block text-brand-primary font-bold uppercase text-xs tracking-widest mb-2">Email</span>
                      <a href="mailto:connect@list.academy" className="text-lg font-bold hover:text-brand-primary transition-colors">connect@list.academy</a>
                    </div>
                    <div>
                      <span className="block text-brand-primary font-bold uppercase text-xs tracking-widest mb-2">Office</span>
                      <address className="not-italic text-sm text-gray-300 leading-relaxed font-medium">
                        405, A wing, Samartha Aishwarya,<br />
                        Lokhandwala, Andheri (West),<br />
                        Mumbai - 400053
                      </address>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
